import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonCard } from '../../../../../core/components/atoms/ButtonCard';
import { iOption, Select } from '../../../../../core/components/atoms/Select';
import { Modal } from '../../../../../core/components/templates/Modal';
import { getDisciplineName } from '../../../../../core/utils/discipline';
import { useCourseHasSimulationWeek } from '../../../hooks/useCourseHasSimulationWeek';
import { useFetchContent } from '../../../hooks/useFetchContent';
import { useMarkContentAsDone } from '../../../hooks/useMarkContentAsDone';
import { useMarkContentAsSelected } from '../../../hooks/useMarkContentAsSelected';
import { Course } from '../../../models/Course.model';
import { useCoursesStore } from '../../../stores/courseStore';
import { AccordionContentList } from '../../AccordionContentList';
import { CourseContentList } from '../CourseContentList';

interface CourseContentModalProps {
  selectedProgramObject: any;
  selectedContentObject?: any;
  course: Course;
  initialDisciplineId?: string;
  redirectOnClick?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

function CourseContentModal({
  selectedProgramObject,
  selectedContentObject,
  initialDisciplineId,
  redirectOnClick = false,
  course,
  isOpen,
  onClose,
}: CourseContentModalProps) {
  const navigate = useNavigate();
  const markContentAsDone = useMarkContentAsDone();

  const fetchContent = useFetchContent();
  const markContentAsSelected = useMarkContentAsSelected();
  const { setIsLoadingContent, setLoadedCourse, loadedCourse } =
    useCoursesStore();

  const [selectedProgram, setSelectedProgram] = useState<any>(
    selectedProgramObject
  );

  const courseHasSimulationWeek = useCourseHasSimulationWeek(
    course.slug,
    selectedProgram.id
  );

  const disciplinesOptions: iOption[] = useMemo(() => {
    const options: iOption[] = [];
    selectedProgram.content.forEach((content: any) => {
      const exists = options.some(
        (discipline) => discipline.value === content.area.id
      );
      if (!exists) {
        options.push({
          value: content.area.id,
          label: getDisciplineName(content.area.slug),
        });
      }
    });
    return options.sort((a, b) => {
      if (a.label > b.label) {
        return 1;
      }
      if (a.label < b.label) {
        return -1;
      }
      return 0;
    });
  }, [selectedProgram.content]);

  const [selectedDiscipline, setSelectedDiscipline] = useState<
    iOption | undefined
  >();

  const [normalContentList, setNormalContentList] = useState<any[]>([]);
  const [supportContentList, setSupportContentList] = useState<any[]>([]);
  const [beyondContentList, setBeyondContentList] = useState<any[]>([]);

  const filterContentByArea = useCallback(
    (areaId: string): any[] => {
      return selectedProgram.content.filter((content: any) => {
        return content.area.id === areaId;
      });
    },
    [selectedDiscipline, selectedProgram, selectedProgramObject]
  );

  function handleFilterChange(option: iOption) {
    setSelectedDiscipline(option);
    updateList(filterContentByArea(option.value.toString()));
  }

  function updateList(contentList: any) {
    setNormalContentList(
      contentList.filter((item: any) => item.category === 'normal')
    );
    setSupportContentList(
      contentList.filter((item: any) => item.category === 'support')
    );
    setBeyondContentList(
      contentList.filter((item: any) => item.category === 'beyond')
    );
  }

  function handleDoneContent(content: any) {
    if (content.done) {
      return;
    }

    const contentParams = {
      courseId: course._id,
      programId: selectedProgramObject.id,
      contentId: content._contentId,
    };

    markContentAsDone.mutate(contentParams, {
      onSuccess: (data: any) => {
        const mewSelectedProgram = {
          ...selectedProgram,
        };

        const contentIndex = mewSelectedProgram.content.findIndex(
          (item: any) => {
            return item._contentId === contentParams.contentId;
          }
        );

        mewSelectedProgram.content[contentIndex].done = data.content.done;

        setSelectedProgram(mewSelectedProgram);
      },
    });
  }

  function handleOpenContent(content: any) {
    if (content.type === 'Link') {
      window.open(content.value, '_blank');

      if (!content.done) {
        handleDoneContent(content);
      }
      return;
    }

    const contentParams = {
      courseId: course._id,
      programId: selectedProgramObject.id,
      contentId: content._contentId,
    };
    markContentAsSelected.mutate(contentParams);

    if (redirectOnClick) {
      window.location.href = `/cursos/${course.slug}`;
      return;
    }

    setIsLoadingContent(true);

    onClose();
    fetchContent.mutate(contentParams, {
      onSuccess: (data) => {
        const newLoadedCourse = {
          ...loadedCourse,
          selectedContentObject: data,
          selectedContent: data._contentId,
        };
        setLoadedCourse(newLoadedCourse);
        setIsLoadingContent(false);
      },
    });
  }

  useEffect(() => {
    window.addEventListener('setContentAsDone', setContentAsDone);

    return () => {
      window.removeEventListener('setContentAsDone', setContentAsDone);
    };
  }, []);

  function setContentAsDone(event: Event) {
    event.stopImmediatePropagation();
    const customEvent = event as CustomEvent<any>;

    const contentId = customEvent.detail.contentId;
    const mewSelectedProgram = {
      ...selectedProgram,
    };

    const contentIndex = mewSelectedProgram.content.findIndex((item: any) => {
      return item._contentId === contentId;
    });

    if (contentIndex === -1) {
      return;
    }

    mewSelectedProgram.content[contentIndex].done = true;

    setSelectedProgram(mewSelectedProgram);
  }

  useEffect(() => {
    setSelectedProgram(selectedProgramObject);
    let contentList;
    if (selectedContentObject) {
      contentList = filterContentByArea(selectedContentObject.area.id);
    } else {
      const disciplineId =
        initialDisciplineId !== undefined
          ? initialDisciplineId
          : disciplinesOptions[0].value.toString();
      contentList = filterContentByArea(disciplineId);
    }

    updateList(contentList);
    onClose();
  }, [selectedContentObject, selectedProgramObject, initialDisciplineId]);

  useEffect(() => {
    if (disciplinesOptions.length > 0) {
      const disciplineId =
        initialDisciplineId !== undefined
          ? initialDisciplineId
          : selectedContentObject.area.id;
      let discipline = disciplinesOptions.find(
        (option) => option.value === disciplineId
      );

      if (!discipline) {
        discipline = disciplinesOptions[0];
      }

      setSelectedDiscipline(discipline);
    }
  }, [selectedContentObject, disciplinesOptions, initialDisciplineId]);

  useEffect(() => {
    if (selectedDiscipline) {
      localStorage.setItem(
        `at:lastSelectedDiscipline:${course._id}`,
        `${selectedDiscipline.label}`
      );
      updateList(filterContentByArea(selectedDiscipline.value.toString()));
    }
  }, [selectedProgram, selectedDiscipline]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      position='top-right'
      title={selectedProgramObject ? selectedProgram.title : ''}
      subtitle={course ? course.title : ''}
      className={`
        m-0 
        mt-[10vh]
        
        min-h-[90vh]

        max-w-full 
        rounded-b-none 
        bg-color-modal-conteudo 
        md:m-4
        md:min-h-[calc(100%-2rem)]
        md:max-w-lg 

        md:rounded-b-lg
      `}
    >
      <div className={`mt-4 flex flex-col gap-xxl`}>
        {!courseHasSimulationWeek.isLoading &&
          courseHasSimulationWeek.data.existSimulationWeekInCourse && (
            <ButtonCard
              title='Simulado Semanal de Revisão (SSR)'
              subtitle={`Revise os conteúdos vistos até a semana ${
                selectedProgram.order + 1
              } do curso`}
              icon={{
                Icon: 'timer',
                type: 'material',
                className: 'text-button-color-tertiary-default',
              }}
              onClick={() => {
                navigate(`/cursos/${course.slug}/revisao`);
              }}
            />
          )}

        {disciplinesOptions.length > 1 && (
          <Select
            options={disciplinesOptions}
            value={selectedDiscipline}
            onChange={handleFilterChange}
          />
        )}

        <CourseContentList
          contentList={normalContentList}
          onClickDone={handleDoneContent}
          handleOpenContent={handleOpenContent}
        />

        {supportContentList.length > 0 && (
          <AccordionContentList
            contentList={supportContentList}
            selectedContentId={selectedContentObject?._contentId}
            onClickDone={handleDoneContent}
            handleOpenContent={handleOpenContent}
            title='Materiais de apoio'
          />
        )}

        {beyondContentList.length > 0 && (
          <AccordionContentList
            selectedContentId={selectedContentObject?._contentId}
            contentList={beyondContentList}
            onClickDone={handleDoneContent}
            handleOpenContent={handleOpenContent}
            title='Indo além'
          />
        )}
      </div>
    </Modal>
  );
}

export { CourseContentModal };
